import { useCallback, useEffect, useMemo, useRef } from 'react';
import Tooltip from 'common/components/tooltip';
import { useTheme } from '@mui/styles';
import { useTicketLockStatus } from 'remote-state/ticketServiceHooks';
import useAnalytics from 'common/utils/hooks/useAnalytics';
import { TRACK_EVENTS } from 'constants/trackEvents';
import useValidateSrLockBeforeUpdate from '../hooks/useValidateSrLockBeforeUpdate';
import { StyledIsNotTemplate } from './style';
import useGetFieldPermissions from '../../../../common/utils/hooks/useGetFieldPermissions';
import { COLUMN_CELL_TYPES, DROP_DOWN_DELAY } from '../constants';
import { FIELD_TYPE } from '../../../../common/components/grid/constants';
import BooleanField from '../../../srPanel/templateFieldsGrid/booleanField';
import { InlineFieldContainer } from '../../../../common/components/controls/InlineField/InlineFieldContainer';
import { RICH_TEXT_FIELD_IDS } from '../../../srPanel/consts';
import { RichTextRender } from './RichTextRender';
import { TextAreaRender } from './TextAreaRender';
import IdFieldRender from './IdFieldRender';
import DatePicker from '../../../../common/components/datePicker';
import { Timer } from '../../../../common/components/timer';
import DueDateField from '../../../../common/components/dueDate';
import { TO_BE_DETERMINED } from '../../../header/categories/constants';
import { CategoryRender } from './CategoryRender/CategoryRender';
import { SelectRender } from './SelectRender';
import { AssigneeRender } from './AssigneeRender';
import OpenTicketCell from './OpenTicket';
import { BarLoader, UserLoader } from './SkeletonLoader';
import CellComponent from './CellComponent';

const CustomLoader = ({ fieldTypeName }) => {
  if (fieldTypeName === 'ASSIGNEE') return <UserLoader />;
  return <BarLoader />;
};

const DefaultCellRender = (props) => {
  const { column, data: rowData, rowIndex, api, context } = props;
  const {
    showNoPermissionPopup,
    isSROpenedFromQueue,
    activeView,
    texts,
    checkAndUpdateSr,
    username,
    assistiveAI,
    copilotPlanFlags,
  } = context;

  const data = rowData?.filter(Boolean) ?? null;

  const isAiEnabled = copilotPlanFlags?.copilotForAgentsEnabled;
  const enableAiSummary = isAiEnabled && (assistiveAI?.AICaseSummarizations || assistiveAI?.AIEmotions);
  const { mandatoryFieldError, notExistInTemplateMessage } = texts ?? {
    mandatoryFieldError: '',
    notExistInTemplateMessage: '',
  };
  const theme = useTheme();
  const handleSaveSrTimeout = useRef();

  const srId = useMemo(() => (data?.length && data?.find((fields) => fields.field === 'id')?.value) || null, [data]);

  // TODO this must be moved out
  const { data: lockingDetails } = useTicketLockStatus(srId);

  const isLocked = lockingDetails?.isLocked;
  const lockingUser = lockingDetails?.lockingUser;
  const checkLockedUser = isLocked && username !== lockingUser;
  const checkAvailableTicket = isLocked && username === lockingUser;
  const hoverEffectText = useMemo(() => ({ propertyName: 'cursor', value: 'text', priority: 'important' }), []);
  const hoverEffectPointer = useMemo(() => ({ propertyName: 'cursor', value: 'pointer', priority: 'important' }), []);

  const activeField = useMemo(
    () => (data?.length && data?.find((fields) => fields.field === column.colDef.field)) || null,
    [data, column],
  );
  const activeFieldConfig = activeView?.columnsConfig?.columnConfiguration?.find(
    (config) => config.id === column.colDef.fieldId,
  );
  const srStatusValue = useMemo(
    () => (Array.isArray(data) ? String(data?.find((fields) => fields.field === 'status')?.value) : undefined),
    [data],
  );
  const fieldPermission = useGetFieldPermissions(activeField);
  const fieldNotInTemplate = activeField && !activeField?.inTemplate;
  const isNoEditPermission = activeField?.readOnly || fieldPermission?.disabled;
  const disableField = fieldNotInTemplate || checkLockedUser || isNoEditPermission;

  const trackEvents = useAnalytics();

  const handleSaveSR = useCallback(
    async (object) => {
      const objectToUpdate = { id: srId, requestParams: object, queueUpdate: true };
      await checkAndUpdateSr(objectToUpdate);
    },
    [srId, checkAndUpdateSr],
  );

  const { handleUpdateSR } = useValidateSrLockBeforeUpdate({ srId, handleSaveSR });

  const handleInlineFieldChange = useCallback(
    (newObject) => {
      handleUpdateSR(newObject).then();
      const fieldName = activeFieldConfig?.fieldName;
      const fieldValue =
        activeField?.richText && newObject.richTextFields
          ? newObject.richTextFields
          : newObject[Object.keys(newObject)?.[0] ?? ''];

      if (activeFieldConfig?.customColumn) {
        trackEvents(TRACK_EVENTS.CUSTOM_COLUMN_POPULATED, {
          location: 'queue',
          srId,
          fieldName,
          fieldValue,
        });
      }
    },
    [handleUpdateSR, trackEvents, activeField, activeFieldConfig, srId],
  );

  const handleFieldChange = useCallback(
    async (newValue) => {
      const object = { [column.colId]: newValue };
      handleInlineFieldChange(object);
    },
    [handleInlineFieldChange, column.colId],
  );

  useEffect(
    () => () => {
      clearTimeout(handleSaveSrTimeout.current);
    },
    [],
  );

  // Temporary solution for displaying due date field on the queue page.
  // TODO: Fix the rendering condition of the DueDate component, after BE will add a new field.
  const fieldTypeName = useMemo(() => {
    let name = column.colDef.fieldTypeName;
    if (name === 'DATE' && column.colId === 'dueDate') {
      name = 'TIME_REMAINING';
    }
    if (name === 'DATE' && column.colDef.sysaidEntity === 'timers') {
      name = 'TIMER';
    }
    return name;
  }, [column]);

  const { endDate, startDate } = useMemo(() => {
    if (!data?.length) return { endDate: undefined, startDate: undefined };
    return data.reduce(
      (result, fields) => {
        if (fields.field === 'dueDate') {
          result.endDate = fields;
        }
        if (fields.field === 'insertTime') {
          result.startDate = fields;
        }
        return result;
      },
      { endDate: undefined, startDate: undefined },
    );
  }, [data]);

  const srType = useMemo(() => data?.find((fields) => fields.field === 'srType')?.value ?? undefined, [data]);

  const selectRenderStyle = useMemo(
    () => ({
      fontSize: theme.fontSize.main,
      color: theme.palette.text.txt_default,
      minWidth: 90,
      minHeight: 24,
    }),
    [theme.fontSize.main, theme.palette.text.txt_default],
  );

  const categoryRenderStyle = useMemo(
    () => ({
      fontSize: theme.fontSize.main,
      lineHeight: 1.429,
      color: theme.palette.text.txt_default,
    }),
    [theme.fontSize.main, theme.palette.text.txt_default],
  );

  const richTextFieldValue = useMemo(
    () => ({
      jsonContent: activeField?.jsonContent ? JSON.parse(activeField.jsonContent) : null,
      plainTextContent: activeField?.plainTextContent || null,
    }),
    [activeField?.jsonContent, activeField?.plainTextContent],
  );

  const field = useMemo(
    () => ({
      fieldName: column?.colId,
      fieldId: column.colDef.fieldId,
      templateFieldAttributes: {
        ticketTemplateFieldType: {
          id: column.colDef.fieldTypeId,
        },
      },
    }),
    [column?.colId, column.colDef.fieldId, column.colDef.fieldTypeId],
  );

  const isSelector = [COLUMN_CELL_TYPES.SELECT, COLUMN_CELL_TYPES.MULTISELECT].includes(fieldTypeName);

  const isFullWidth =
    [COLUMN_CELL_TYPES.LONGTEXT, COLUMN_CELL_TYPES.MULTISELECT].includes(fieldTypeName) ||
    (fieldTypeName === COLUMN_CELL_TYPES.SELECT &&
      ![FIELD_TYPE.assignee, FIELD_TYPE.priority, FIELD_TYPE.status].includes(column.colId));

  const getCellComponent = useMemo(() => {
    switch (fieldTypeName) {
      case COLUMN_CELL_TYPES.BOOLEAN:
        return (
          <BooleanField
            key={srId}
            field={field}
            required={false}
            fieldValue={activeField?.value}
            disabled={disableField}
            onChange={handleFieldChange}
            isTableView
            texts={texts}
          />
        );
      case COLUMN_CELL_TYPES.TEXT:
        return (
          <InlineFieldContainer
            key={srId}
            isAiEnabled={isAiEnabled}
            assistiveAI={assistiveAI}
            fullwidth
            texts={texts}
            limit={250}
            delayedHover={500}
            required={false}
            multiline={false}
            disabled={disableField}
            fieldName={column.colId}
            hoverEffect={hoverEffectText}
            tooltipClassName="inline-field-tooltip"
            handleSaveValue={handleInlineFieldChange}
            showSummaryTooltip={column.colId === 'title' && enableAiSummary}
            text={typeof activeField?.value === 'object' ? activeField?.value?.title : activeField?.value}
            disableTooltip
            isNoEditPermission={isNoEditPermission}
            {...(isNoEditPermission ? { onClick: showNoPermissionPopup } : {})}
          />
        );
      case COLUMN_CELL_TYPES.LONGTEXT:
        if (RICH_TEXT_FIELD_IDS.includes(column.colDef.fieldId))
          return (
            <RichTextRender
              key={srId}
              isAiEnabled={isAiEnabled}
              assistiveAI={assistiveAI}
              srId={srId}
              required={false}
              className="isQueuePage"
              disabled={disableField}
              fieldName={column.colId}
              fieldId={column.colDef.fieldId}
              dataTestId="sr-description-editor"
              handleSaveValue={handleInlineFieldChange}
              fieldValue={richTextFieldValue}
              notInTemplate={fieldNotInTemplate}
              onDisabledClick={showNoPermissionPopup}
              disableTooltip
              isNoEditPermission={isNoEditPermission}
            />
          );

        return (
          <TextAreaRender
            key={srId}
            isAiEnabled={isAiEnabled}
            assistiveAI={assistiveAI}
            required={false}
            disabled={disableField}
            fieldName={column.colId}
            handleSaveValue={handleInlineFieldChange}
            text={activeField?.value}
            notInTemplate={fieldNotInTemplate}
            texts={texts}
            isNoEditPermission={isNoEditPermission}
            showNoPermissionPopup={showNoPermissionPopup}
          />
        );
      case COLUMN_CELL_TYPES.NUMBER:
        if (column.colDef.field === 'id') {
          return <IdFieldRender key={srId} srId={srId} isAiEnabled={isAiEnabled} assistiveAI={assistiveAI} />;
        }
        return (
          <InlineFieldContainer
            key={srId}
            isAiEnabled={isAiEnabled}
            assistiveAI={assistiveAI}
            fullwidth
            limit={250}
            type="number"
            delayedHover={500}
            required={false}
            multiline={false}
            disabled={disableField}
            fieldName={column.colId}
            text={activeField?.value}
            hoverEffect={hoverEffectText}
            handleSaveValue={handleInlineFieldChange}
            notInTemplate={fieldNotInTemplate}
          />
        );
      case COLUMN_CELL_TYPES.DATE:
      case COLUMN_CELL_TYPES.DATEANDTIME:
        return (
          <DatePicker
            key={srId}
            fullwidth
            delayedHover={500}
            required={false}
            disablePortal={false}
            disabled={disableField}
            fieldType={column.colDef.fieldTypeId}
            fieldName={column.colId}
            date={activeField?.value}
            onChange={handleFieldChange}
            hoverEffect={hoverEffectPointer}
            emptyValueTooltip={mandatoryFieldError}
            notInTemplate={fieldNotInTemplate}
            isSROpenedFromQueue={isSROpenedFromQueue}
            queueView
          />
        );
      case COLUMN_CELL_TYPES.TIMER:
        return <Timer key={srId} disabled={disableField} milliseconds={activeField?.value} />;
      case COLUMN_CELL_TYPES.TIME_REMAINING: {
        return (
          <DueDateField
            key={srId}
            srId={srId}
            delayedHover={500}
            required={false}
            disabled={disableField}
            notInTemplate={fieldNotInTemplate}
            delay={DROP_DOWN_DELAY}
            endDate={endDate?.value}
            fieldName={column?.colId}
            startDate={startDate?.value}
            srStatusValue={srStatusValue}
            onSaveDate={handleFieldChange}
            hoverEffect={hoverEffectPointer}
            emptyValueTooltip={mandatoryFieldError}
            templateNotFoundMessage={notExistInTemplateMessage}
          />
        );
      }
      case COLUMN_CELL_TYPES.SELECT:
      case COLUMN_CELL_TYPES.MULTISELECT: {
        if (
          column.colDef.field === 'primaryCategory' ||
          column.colDef.field === 'secondaryCategory' ||
          column.colDef.field === 'thirdLevelCategory'
        ) {
          const category =
            data?.length > 0 && activeField?.categoryCaption !== TO_BE_DETERMINED
              ? data?.find((fields) => fields.field === props.column?.colId)
              : undefined;

          return (
            <CategoryRender
              key={srId}
              srId={srId}
              srType={srType}
              delayedHover={500}
              rowData={category}
              required={false}
              disabled={disableField}
              isNoEditPermission={isNoEditPermission}
              delay={DROP_DOWN_DELAY}
              value={activeField?.value}
              fieldId={column.colDef.fieldId}
              hoverEffect={hoverEffectPointer}
              headerName={column.colDef.headerName}
              listKey={column.colDef.filterParams.listKey}
              style={categoryRenderStyle}
              rowIndex={rowIndex}
              categoryCaption={category?.categoryCaption}
            />
          );
        }
        if (column.colDef.field !== 'assignee') {
          return (
            <SelectRender
              key={srId}
              srId={srId}
              srType={srType}
              delayedHover={500}
              required={false}
              delay={DROP_DOWN_DELAY}
              disabled={disableField}
              isNoEditPermission={isNoEditPermission}
              hoverEffect={hoverEffectPointer}
              fieldId={column?.colDef?.fieldId}
              customColumn={column?.colDef?.customColumn}
              fieldName={column?.colDef?.field}
              headerName={column.colDef.headerName}
              keyString={column.colDef.keyString}
              captionString={column.colDef.captionString}
              isMultiple={fieldTypeName === COLUMN_CELL_TYPES.MULTISELECT}
              value={activeField?.value}
              style={selectRenderStyle}
              sortBy={column.colDef.sortBy}
              rowIndex={rowIndex}
              notInTemplate={fieldNotInTemplate}
              valueCaption={activeField?.valueCaption}
              isQueueView
            />
          );
        }
        return (
          <AssigneeRender
            key={srId}
            srId={srId}
            delayedHover={500}
            required={false}
            disabled={disableField}
            hoverEffect={hoverEffectPointer}
            initialAssigned={activeField?.value}
            rowIndex={rowIndex}
          />
        );
      }
      case COLUMN_CELL_TYPES.OPEN_LINK:
        return (
          <OpenTicketCell
            key={srId}
            srId={srId}
            activeField={activeField}
            rowIndex={rowIndex}
            username={username}
            checkAvailableTicket={checkAvailableTicket}
            gridApi={api}
          />
        );
      default:
        return (
          <Tooltip
            key={srId}
            isTruncatedText
            placement="bottom"
            text={activeField?.value}
            style={{
              fontSize: theme.fontSize.main,
              lineHeight: 1.429,
              color: theme.palette.text.txt_default,
            }}
          />
        );
    }
  }, [
    activeField,
    api,
    assistiveAI,
    categoryRenderStyle,
    checkAvailableTicket,
    column?.colDef?.captionString,
    column?.colDef?.customColumn,
    column?.colDef?.field,
    column?.colDef?.fieldId,
    column?.colDef?.fieldTypeId,
    column?.colDef?.filterParams?.listKey,
    column?.colDef?.headerName,
    column?.colDef?.keyString,
    column?.colDef?.sortBy,
    column?.colId,
    data,
    disableField,
    enableAiSummary,
    endDate?.value,
    field,
    fieldNotInTemplate,
    fieldTypeName,
    handleFieldChange,
    handleInlineFieldChange,
    hoverEffectPointer,
    hoverEffectText,
    isAiEnabled,
    isNoEditPermission,
    isSROpenedFromQueue,
    mandatoryFieldError,
    notExistInTemplateMessage,
    props.column?.colId,
    richTextFieldValue,
    rowIndex,
    selectRenderStyle,
    showNoPermissionPopup,
    srId,
    srStatusValue,
    srType,
    startDate?.value,
    texts,
    theme.fontSize.main,
    theme.palette.text.txt_default,
    username,
  ]);

  if (!data?.length) return <CustomLoader fieldTypeName={fieldTypeName} />;

  if (!activeField?.inTemplate && fieldTypeName === 'OPEN_TICKET') {
    return (
      <Tooltip
        title={texts.notAvailableFieldText}
        text={<StyledIsNotTemplate>{texts.notAvailableField}</StyledIsNotTemplate>}
        placement="top"
        style={{
          fontSize: theme.fontSize.main,
          lineHeight: 1.429,
          color: theme.palette.text.txt_default,
        }}
      />
    );
  }

  return (
    <CellComponent fieldTypeName={fieldTypeName} isSelector={isSelector} isFullWidth={isFullWidth}>
      {getCellComponent}
    </CellComponent>
  );
};

export default DefaultCellRender;
